import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calendar, CheckCircle2, X, Clock, Phone, Mail, MessageSquare, Settings as SettingsIcon, Save, Loader2, Download } from "lucide-react";

export default function AgencyCalendar() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");
    const [settings, setSettings] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/agency/calendar/appointments"); setItems(r.data.items || []); } catch (e) { /* noop */ }
        try { const s = await api.get("/agency/calendar/settings"); setSettings(s.data); } catch (e) { /* noop */ }
        setLoading(false);
    };
    useEffect(() => { if (user) load(); }, [user]);

    const decide = async (id, action) => {
        if (action === "decline" && !window.confirm("Rifiutare questa richiesta?")) return;
        try { await api.put(`/appointments/${id}/decide`, { action }); await load(); } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
    };

    if (!user) return <div className="pt-32 text-center text-ink-400">Effettua l'accesso.</div>;
    if (user.role !== "agenzia") return <div className="pt-32 text-center text-ink-400 max-w-md mx-auto">Il calendario è riservato agli account agenzia.</div>;

    const now = new Date();
    const filtered = items.filter((a) => {
        if (tab === "pending") return a.status === "pending";
        if (tab === "upcoming") return ["pending", "confirmed"].includes(a.status) && new Date(a.start_at) >= now;
        if (tab === "past") return new Date(a.end_at) < now || ["declined", "auto_declined", "cancelled"].includes(a.status);
        return true;
    });
    const counts = {
        pending: items.filter((a) => a.status === "pending").length,
        upcoming: items.filter((a) => ["pending", "confirmed"].includes(a.status) && new Date(a.start_at) >= now).length,
        past: items.filter((a) => new Date(a.end_at) < now || ["declined", "auto_declined", "cancelled"].includes(a.status)).length,
    };

    // Group upcoming by day for week-style view (ordinati per orario)
    const grouped = {};
    filtered.forEach((a) => {
        const day = a.start_at.slice(0, 10);
        if (!grouped[day]) grouped[day] = [];
        grouped[day].push(a);
    });
    Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.start_at.localeCompare(b.start_at)));

    return (
        <div data-testid="agency-calendar" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="flex items-start justify-between mb-12 flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2"><Calendar size={14} className="text-gold" /><span className="label-eyebrow">Agenzia</span></div>
                    <h1 className="font-serif text-4xl text-ink-50 mt-2">Calendario visite</h1>
                    <p className="text-sm text-ink-400 mt-2">Anti-overlap automatico su tutti i tuoi immobili. Conferma entro 4h o la richiesta si auto-annulla.</p>
                </div>
                <button data-testid="open-settings" onClick={() => setShowSettings(!showSettings)} className="cta-outline px-4 py-2 text-sm flex items-center gap-2"><SettingsIcon size={14} /> Impostazioni</button>
            </div>

            {showSettings && settings && <CalendarSettings settings={settings} onSaved={(s) => { setSettings(s); setShowSettings(false); load(); }} />}

            <div className="flex gap-2 mb-8 border-b border-white/10">
                {[{ k: "upcoming", l: "In arrivo" }, { k: "pending", l: "Da confermare" }, { k: "past", l: "Storico" }].map((t) => (
                    <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-4 py-3 text-sm transition-colors relative ${tab === t.k ? "text-gold" : "text-ink-400 hover:text-ink-100"}`}>
                        {t.l} <span className="ml-1.5 text-[10px] bg-ink-900 px-1.5 py-0.5">{counts[t.k]}</span>
                        {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-px bg-gold" />}
                    </button>
                ))}
            </div>

            {loading ? <div className="text-center text-ink-400"><Loader2 className="animate-spin inline" /> Caricamento...</div>
                : Object.keys(grouped).length === 0 ? <div className="text-center py-16 text-ink-400">Nessun appuntamento {tab === "pending" ? "in attesa di conferma" : tab === "upcoming" ? "in arrivo" : "passato"}.</div>
                : (
                    <div className="space-y-8">
                        {Object.entries(grouped).sort().map(([day, appts]) => (
                            <div key={day} data-testid={`day-${day}`}>
                                <div className="text-xs text-ink-400 uppercase tracking-wider mb-3 capitalize">
                                    {new Date(day).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                                </div>
                                <div className="space-y-2">
                                    {appts.map((a) => <AppointmentCard key={a.id} appt={a} onDecide={decide} />)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
        </div>
    );
}

function AppointmentCard({ appt, onDecide }) {
    const start = new Date(appt.start_at);
    const end = new Date(appt.end_at);
    const STATUS_COLOR = {
        pending: "border-amber-700/40 bg-amber-900/10",
        confirmed: "border-green-700/40 bg-green-900/10",
        declined: "border-red-700/30 bg-red-900/5 opacity-60",
        auto_declined: "border-ink-700 bg-ink-900/40 opacity-50",
        cancelled: "border-ink-700 bg-ink-900/40 opacity-50",
    };
    const STATUS_LABEL = {
        pending: "In attesa conferma", confirmed: "Confermato", declined: "Rifiutato", auto_declined: "Scaduto", cancelled: "Annullato",
    };
    return (
        <div data-testid={`appt-${appt.id}`} className={`p-4 border ${STATUS_COLOR[appt.status] || "border-white/10"} grid grid-cols-12 gap-4 items-center`}>
            <div className="col-span-2 text-center border-r border-white/10 pr-3">
                <div className="font-serif text-2xl text-gold">{start.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                <div className="text-[10px] text-ink-400 uppercase">→ {end.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <div className="col-span-5">
                <div className="font-serif text-base text-ink-50 truncate">{appt.property_title}</div>
                <div className="text-xs text-ink-400 truncate mt-0.5">Cliente: <span className="text-ink-100">{appt.client_name}</span></div>
                {appt.note && <div className="text-[11px] text-ink-400 italic mt-2 line-clamp-2">"{appt.note}"</div>}
            </div>
            <div className="col-span-3 text-xs space-y-1">
                {appt.client_email && <div className="text-ink-400 flex items-center gap-1.5 truncate"><Mail size={10} /> {appt.client_email}</div>}
                {appt.client_phone && <div className="text-ink-400 flex items-center gap-1.5"><Phone size={10} /> {appt.client_phone}</div>}
                <div className="text-[10px] uppercase tracking-wider mt-1.5 inline-block px-1.5 py-0.5 bg-black/40">{STATUS_LABEL[appt.status]}</div>
            </div>
            <div className="col-span-2 flex justify-end gap-1">
                {appt.status === "confirmed" && (
                    <a
                        data-testid={`ical-${appt.id}`}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${appt.id}/ical`}
                        className="p-2 border border-gold/30 text-gold hover:bg-gold/10"
                        title="Scarica .ics per Google/Apple Calendar"
                    >
                        <Download size={14} />
                    </a>
                )}
                {appt.status === "pending" && (
                    <>
                        <button data-testid={`confirm-${appt.id}`} onClick={() => onDecide(appt.id, "confirm")} className="p-2 bg-gold text-black hover:bg-gold-soft" title="Conferma"><CheckCircle2 size={14} /></button>
                        <button data-testid={`decline-${appt.id}`} onClick={() => onDecide(appt.id, "decline")} className="p-2 border border-red-700/40 text-red-300 hover:bg-red-900/20" title="Rifiuta"><X size={14} /></button>
                    </>
                )}
                {appt.status === "confirmed" && new Date(appt.start_at) > new Date() && (
                    <button data-testid={`cancel-${appt.id}`} onClick={() => onDecide(appt.id, "cancel")} className="p-2 border border-white/15 text-ink-400 hover:text-red-300" title="Annulla"><X size={14} /></button>
                )}
            </div>
        </div>
    );
}

function CalendarSettings({ settings, onSaved }) {
    const [s, setS] = useState({
        slot_duration_min: settings.slot_duration_min,
        buffer_min: settings.buffer_min,
        auto_decline_hours: settings.auto_decline_hours,
        advance_min_hours: settings.advance_min_hours,
        advance_max_days: settings.advance_max_days,
        working_hours: settings.working_hours,
    });
    const [saving, setSaving] = useState(false);
    const days = [["1", "Lun"], ["2", "Mar"], ["3", "Mer"], ["4", "Gio"], ["5", "Ven"], ["6", "Sab"], ["0", "Dom"]];
    const save = async () => {
        setSaving(true);
        try { const r = await api.put("/agency/calendar/settings", s); onSaved(r.data); }
        catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    return (
        <div data-testid="calendar-settings" className="mb-8 border border-gold/30 p-6 bg-ink-900/40">
            <h3 className="font-serif text-xl text-ink-50 mb-5">Impostazioni calendario</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div>
                    <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Durata slot</label>
                    <select data-testid="setting-duration" value={s.slot_duration_min} onChange={(e) => setS({ ...s, slot_duration_min: parseInt(e.target.value) })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm">
                        {[30, 45, 60, 90, 120].map((v) => <option key={v} value={v} className="bg-ink-950">{v} min</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Buffer tra visite</label>
                    <input data-testid="setting-buffer" type="number" min={0} value={s.buffer_min} onChange={(e) => setS({ ...s, buffer_min: parseInt(e.target.value || "0") })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                </div>
                <div>
                    <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Auto-rifiuto (ore)</label>
                    <input type="number" min={1} value={s.auto_decline_hours} onChange={(e) => setS({ ...s, auto_decline_hours: parseInt(e.target.value || "1") })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                </div>
                <div>
                    <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Min anticipo (ore)</label>
                    <input type="number" min={0} value={s.advance_min_hours} onChange={(e) => setS({ ...s, advance_min_hours: parseInt(e.target.value || "0") })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                </div>
                <div>
                    <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Max anticipo (gg)</label>
                    <input type="number" min={1} value={s.advance_max_days} onChange={(e) => setS({ ...s, advance_max_days: parseInt(e.target.value || "1") })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                </div>
            </div>
            <div className="mb-6">
                <div className="text-xs text-ink-400 uppercase tracking-wider mb-3">Orari di lavoro</div>
                <div className="space-y-2">
                    {days.map(([k, l]) => {
                        const ranges = s.working_hours[k] || [];
                        return (
                            <div key={k} className="grid grid-cols-12 gap-2 items-center text-xs">
                                <div className="col-span-1 text-ink-100 font-medium">{l}</div>
                                <div className="col-span-10 flex gap-2 flex-wrap">
                                    {ranges.length === 0 ? <span className="text-ink-400 italic">Chiuso</span> : ranges.map((r, i) => (
                                        <div key={i} className="flex items-center gap-1">
                                            <input value={r[0]} onChange={(e) => { const nh = { ...s.working_hours }; nh[k] = [...ranges]; nh[k][i] = [e.target.value, r[1]]; setS({ ...s, working_hours: nh }); }} className="bg-transparent border border-white/15 px-2 py-1 w-16 text-ink-50 text-xs font-mono" placeholder="HH:MM" />
                                            <span className="text-ink-400">→</span>
                                            <input value={r[1]} onChange={(e) => { const nh = { ...s.working_hours }; nh[k] = [...ranges]; nh[k][i] = [r[0], e.target.value]; setS({ ...s, working_hours: nh }); }} className="bg-transparent border border-white/15 px-2 py-1 w-16 text-ink-50 text-xs font-mono" placeholder="HH:MM" />
                                            <button onClick={() => { const nh = { ...s.working_hours }; nh[k] = ranges.filter((_, idx) => idx !== i); setS({ ...s, working_hours: nh }); }} className="text-ink-400 hover:text-red-300 px-1"><X size={12} /></button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => { const nh = { ...s.working_hours }; nh[k] = [...(ranges || []), ["09:00", "13:00"]]; setS({ ...s, working_hours: nh }); }} className="col-span-1 text-xs text-gold hover:underline">+ fascia</button>
                            </div>
                        );
                    })}
                </div>
            </div>
            <button data-testid="save-settings" onClick={save} disabled={saving} className="cta-gold px-5 py-2.5 text-sm flex items-center gap-2"><Save size={14} /> {saving ? "Salvataggio..." : "Salva impostazioni"}</button>
        </div>
    );
}
